import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, Edit2, Search, FileText, Plus, Download } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    API.get('/documents')
      .then(r => setDocs(r.data))
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await API.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded successfully');
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await API.delete(`/documents/${id}`);
      toast.success('Document deleted');
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const filtered = docs.filter(d => d.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 md:p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Documents</h1>
          <p className="text-slate-500 text-sm mt-0.5">{docs.length} document{docs.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
          data-testid="upload-document-btn"
        >
          <Upload size={15} />
          {uploading ? 'Uploading...' : 'Upload Document'}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={handleUpload} className="hidden" data-testid="file-input" />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search documents..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          data-testid="document-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium">{search ? 'No documents match your search' : 'No documents yet'}</p>
            {!search && (
              <button onClick={() => fileRef.current?.click()} className="mt-3 flex items-center gap-1.5 text-blue-700 text-sm hover:underline mx-auto">
                <Plus size={14} /> Upload your first document
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Pages</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Uploaded</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(doc => (
                <tr key={doc.id} className="hover:bg-slate-50 transition-colors" data-testid={`document-row-${doc.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-sm flex items-center justify-center flex-shrink-0">
                        <FileText size={15} className="text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate max-w-xs">{doc.name}</p>
                        <p className="text-xs text-slate-400 uppercase">{doc.file_ext}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-slate-600 hidden sm:table-cell">{doc.page_count || 1}</td>
                  <td className="px-4 py-3.5 text-slate-600 hidden md:table-cell">{formatBytes(doc.file_size)}</td>
                  <td className="px-4 py-3.5 text-slate-600 hidden md:table-cell">{formatDate(doc.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => navigate(`/documents/${doc.doc_id || doc.id}/edit`)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit Fields"
                        data-testid={`edit-document-${doc.id}`}
                      >
                        <Edit2 size={15} />
                      </button>
                      <a
                        href={`${BACKEND}/api/documents/${doc.doc_id || doc.id}/file`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        title="Download"
                        data-testid={`download-document-${doc.id}`}
                      >
                        <Download size={15} />
                      </a>
                      <button
                        onClick={() => handleDelete(doc.doc_id || doc.id, doc.name)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                        data-testid={`delete-document-${doc.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
